import LogRocket from "logrocket";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import Header from "./Components/Header/Header";
import SnackBar from "./Components/SnackBar/SnackBar";
import useComplaints from "./CustomHooks/useComplaints";
import useLocations from "./CustomHooks/useLocations";
import { useNotifications } from "./CustomHooks/useNotifications";
import { useProcedures } from "./CustomHooks/useProcedure";
import { useProcedureHistory } from "./CustomHooks/useProcedureHistory";
import { useReports } from "./CustomHooks/useReports";
import useWorkForce from "./CustomHooks/useWorkForce";
import { getTotalComplaints } from "./Redux/complaints/action";
import { getComplaintSummary } from "./Redux/locations/action";
import { setTableFilter } from "./Redux/table/action";
import { getOperationsData, getRolesData } from "./Redux/workforce/action";
import Home from "./Screens/Home/Home";
import Login from "./Screens/Login/Login";
import Profile from "./Screens/Profile/Profile";
import SetPassword from "./Screens/SetPassword/SetPassword";
import Test from "./Screens/Test/Test";
import { useAuthSubscriber } from "./Services/authentication";
import {
  useCaptureSummaryListener,
  useDevicesListener,
  usePicturesListener,
  useProfileListener,
  useTracksListener
} from "./Services/database";
import { isValidObject } from "./Services/validators";
import { rippleEffect } from "./Utils/Constants/Constants";

function App(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [isAuth, setIsAuth] = useState(null);
  const [claims, setClaims] = useState(null);

  useAuthSubscriber(({ isAuth, claims }) => {
    setIsAuth(isAuth);
    setClaims(claims);
  }, isAuth);

  useEffect(() => {
    console.log(claims, "claims");
  }, [claims]);

  useProfileListener({
    uid: props.auth.data.uid,
    contactInfo: props.auth.data.phoneNumber || props.auth.data.email,
    isAuth: props.auth.data.uid && isAuth
  });

  const locationHooks = useLocations({
    accessToken: props.auth?.data.accessToken,
    isAuth: props.auth.data.uid && isAuth,
    filterData: props.table.locations.filter,
    searchKey: props.table.locations.searchKey,
    captureSummaryData: props.workforce.captureSummary,
    sort: props.table.locations.sort
  });

  useProcedures({
    isAuth: props.auth.data.uid && isAuth && claims?.procedures?.read,
    filterData: props.table.procedures.filter,
    searchKey: props.table.procedures.searchKey,
    sort: props.table.procedures.sort
  });

  const workforceHooks = useWorkForce({
    accessToken: props.auth?.data.accessToken,
    filterData: props.table.workforce.filter,
    isAuth: props.auth.data.uid && isAuth && claims?.employees?.read,
    locationData: props.locations.data,
    searchKey: props.table.workforce.searchKey,
    sort: props.table.workforce.sort,
    roles: isValidObject(props.workforce.roles) && props.workforce.roles,
    validRoles:
      isValidObject(props.workforce.roles) &&
      Object.values(props.workforce.roles)
        ?.filter((role) => isValidObject(role.attendance?.capture))
        .map((role) => role.documentId)
  });

  const complaintsHooks = useComplaints({
    accessToken: props.auth?.data.accessToken,
    filterData: props.table.complaints.filter,
    isAuth: props.auth.data.uid && isAuth && claims?.complaints?.read?.all,
    locationData: props.locations.data,
    searchKey: props.table.complaints.searchKey,
    selectedComplaint: props.table.complaints.selectedComplaint,
    sort: props.table.complaints.sort,
    pageNumber: props.table.complaints.pageNumber
  });

  const procedureHistoryHooks = useProcedureHistory({
    accessToken: props.auth?.data.accessToken,
    filterData: props.table.procedureHistory.filter,
    isAuth: props.auth.data.uid && isAuth && claims?.procedureHistory?.read,
    searchKey: props.table.procedureHistory.searchKey,
    selectedProcedureHistory:
      props.table.procedureHistory.selectedProcedureHistory,
    sort: props.table.procedureHistory.sort,
    locationData: props.locations.data
  });

  const { notification } = useNotifications({
    isAut: props.auth.data.uid && isAuth,
    uid: props.auth?.data?.uid
  });

  useReports({
    isAuth: props.auth.data.uid && isAuth && claims?.files?.read,
    filterData: props.table.reports.filter,
    searchKey: props.table.reports.searchKey,
    sort: props.table.reports.sort
  });

  useTracksListener({
    isAuth: props.auth.data.uid && isAuth,
    filterData: props.table.tracks.filter
  });
  useCaptureSummaryListener({
    isAuth: props.auth.data.uid && isAuth && claims?.surveillance?.read
  });
  usePicturesListener({
    isAuth: props.auth.data.uid && isAuth,
    isAllowedToRead: claims?.surveillance?.read
  });

  useDevicesListener({
    isAuth: props.auth.data.uid && isAuth && claims?.surveillance?.read
  });

  useEffect(() => {
    if (
      process.env.NODE_ENV === "production" &&
      window.location.hostname !== "localhost"
    ) {
      if (process.env.REACT_APP_LOG_ROCKET_KEY) {
        LogRocket.init(process.env.REACT_APP_LOG_ROCKET_KEY);
      }
    }
  }, []);

  useEffect(() => {
    setStatus(props.status.message || null);
  }, [props.status]);

  useEffect(() => {
    if (!(isAuth && props.auth.data.uid)) {
      return;
    }

    if (claims?.complaints?.read?.all) {
      props.getComplaintSummary();
    }
    props.getTotalComplaints(props.locations.data);
    props.getRolesData();
    // eslint-disable-next-line
  }, [isAuth, props.auth.data.uid, props.table.complaints.filter, claims]);

  useEffect(() => {
    const { theme } = props.auth;
    Object.keys(theme).forEach((key) => {
      document.documentElement.style.setProperty(`--${key}`, theme[key]);
    });
    // eslint-disable-next-line
  }, [props.auth.theme]);

  useEffect(() => {
    if(isAuth){
      getOperationsData();
    }
  }, [isAuth]);

  return (
    <div className="font-color-tertiary-dark background-color-primary inherit-parent-height inherit-parent-width">
      {isAuth === null ? (
        <div data-cy="home-loading" />
      ) : (
        <>
          {window.location.pathname !== "/login" &&
            window.location.pathname !== "/setPassword" && (
              <Header
                isSupportActive={location.pathname.startsWith("/support")}
                onSupportClick={(e) => {
                  rippleEffect(e);
                  navigate(`/support`);
                }}
                onNotificationClick={(e) => {
                  rippleEffect(e);
                  navigate(`?force=notification`);
                }}
                onProfileClick={(e) => {
                  rippleEffect(e);
                  navigate(`?force=setting`);
                }}
                name={props.profile?.data?.name}
                theme={
                  props.auth.theme.primaryColor === "#0E0E0E" ? "dark" : "light"
                }
                showNotificationDot={props.notifications?.counts?.unread > 0}
              />
            )}
          <div className="inherit-parent-width remaining-body-height-from-header">
            <Routes>
              <Route
                path="/login"
                element={
                  <PublicRoute isAuth={isAuth}>
                    <Login
                      navigate={navigate}
                      theme={
                        props.auth.theme.primaryColor === "#0E0E0E"
                          ? "dark"
                          : "light"
                      }
                    />
                  </PublicRoute>
                }
              />
              <Route
                path="/setPassword"
                element={
                  <PublicRoute isAuth={isAuth}>
                    <SetPassword
                      navigate={navigate}
                      theme={
                        props.auth.theme.primaryColor === "#0E0E0E"
                          ? "dark"
                          : "light"
                      }
                    />
                  </PublicRoute>
                }
              />
              <Route path="/test" element={<Test navigate={navigate} />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute isAuth={isAuth}>
                    <Home
                      claims={claims}
                      isAuth={isAuth}
                      navigate={navigate}
                      {...locationHooks}
                      {...workforceHooks}
                      {...complaintsHooks}
                      {...procedureHistoryHooks}
                      notification={notification}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute isAuth={isAuth}>
                    <Profile navigate={navigate} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="*"
                element={
                  <ProtectedRoute isAuth={isAuth}>
                    <Navigate to="/" replace />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
          <SnackBar
            message={status}
            status={props.status}
            type={props.status.code === null ? "success" : "error"}
          />
        </>
      )}
    </div>
  );
}

const mapStateToProps = function(state) {
  return {
    auth: state.auth,
    profile: state.profile,
    locations: state.locations,
    status: state.status,
    table: state.table,
    workforce: state.workforce,
    complaints: state.complaints,
    surveillance: state.surveillance,
    notifications: state.notifications
  };
};

const mapDispatchToProps = function() {
  return {
    setTableFilter: (table, filter) => setTableFilter(table, filter),
    getTotalComplaints: (data) => getTotalComplaints(data),
    getComplaintSummary: () => getComplaintSummary(),
    getRolesData: () => getRolesData()
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);

const ProtectedRoute = ({ isAuth, children }) => {
  return isAuth ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ isAuth, children }) => {
  return !isAuth ? children : <Navigate to="/" />;
};
